import { Component, Input, Output, EventEmitter, HostListener, ElementRef, ViewChild } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TemperatureService } from '../temperature.service';

@Component({
  selector: 'app-toggle',
  templateUrl: './toggle.component.html',
  styleUrls: ['./toggle.component.scss'],
  standalone: true,
  imports: [CommonModule, TitleCasePipe, FormsModule]
})
export class ToggleComponent {
  /** Active top-level surface. In code/writing the Tools menu is hidden and only
   *  the temperature slider is shown. */
  @Input() appMode: 'standard' | 'code' | 'writing' | 'image' = 'standard';
  @Output() modeChanged = new EventEmitter<{ mode: string, event: Event }>();
  @Output() contextUpdated = new EventEmitter<string>();
  @Output() temperatureChange = new EventEmitter<number>();
  @ViewChild('popup') popup!: ElementRef;
  @ViewChild('toolsButton') toolsButton!: ElementRef;

  /** True in a committed surface mode — hide Tools, show the temperature dial. */
  get showInlineTemperature(): boolean {
    return this.appMode === 'code' || this.appMode === 'writing' || this.appMode === 'image';
  }

  selectedModes: string[] = []; // Initialize with empty array since standard is default
  isPopupVisible: boolean = false;
  isContextContainerVisible: boolean = false;
  isTemperatureContainerVisible: boolean = false;
  contextText: string = '';
  /** Current temperature. Accepts a parent binding; falls back to the service default. */
  @Input() temperature: number = 1.0;
  popupPosition: 'top' | 'bottom' = 'bottom';
  
  constructor(private temperatureService: TemperatureService) {
    this.temperature = this.temperatureService.getDefaultTemperature('generalQuestions');
  }
  
  /** The surface tentatively chosen in the open Tools popup (not yet committed). */
  pendingMode: string | null = null;

  /**
   * Click a surface in the Tools popup. This does NOT immediately switch — it
   * just highlights the choice and keeps the popup open so the user can change
   * their mind (Code ↔ Writing) or back out. Clicking the same option again
   * de-selects it. The choice is committed when the popup closes (apply/outside
   * click), via commitPendingMode().
   */
  selectMode(mode: string) {
    this.pendingMode = this.pendingMode === mode ? null : mode;
  }

  /**
   * Commit the tentative selection (called when the popup closes).
   * Emits the chosen surface, or 'standard' when deselected back to nothing —
   * but only if the committed value differs from the already-active mode.
   */
  private commitPendingMode(): void {
    if (!this.isPopupVisible) return;
    this.isPopupVisible = false;

    const target = this.pendingMode ?? 'standard';
    this.pendingMode = null;

    // Only emit if it actually changes the active surface.
    if (target !== this.appMode) {
      this.modeChanged.emit({ mode: target, event: new Event('') });
    }
  }

  /** "Apply" button in the popup, or Enter — commit immediately. */
  applyPendingMode(event?: Event): void {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    this.commitPendingMode();
  }

  togglePopup(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.isPopupVisible) {
      // Closing via the Tools button — commit any pending selection.
      this.commitPendingMode();
      return;
    }

    this.checkPopupPosition();
    // Pre-select the currently active surface so the user sees where they are
    // and can switch or deselect it.
    this.pendingMode = this.appMode === 'standard' ? null : this.appMode;
    this.isPopupVisible = true;
  }

  /** Toggle the temperature popover. It opens in a fixed spot above the chat input. */
  toggleInlineTemperature(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.isTemperatureContainerVisible = !this.isTemperatureContainerVisible;
  }

  private checkPopupPosition(): void {
    if (!this.toolsButton) return;
    
    // Check if chat input is centered (no messages)
    const chatInputContainer = document.querySelector('.chat-input-container');
    const isCentered = chatInputContainer?.classList.contains('centered');
    
    // If chat is centered (no messages), show popup below
    // If chat is not centered (has messages), show popup above
    this.popupPosition = isCentered ? 'bottom' : 'top';
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (this.isPopupVisible) {
      this.checkPopupPosition();
    }
    // Fixed-positioned temperature popover: close on scroll/resize so it never
    // floats out of place relative to the icon.
    if (this.isTemperatureContainerVisible) {
      this.isTemperatureContainerVisible = false;
    }
  }

  toggleContextContainer(event?: Event): void {
    // Prevent event propagation if event exists
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Close temperature container if it's open
    if (this.isTemperatureContainerVisible) {
      this.isTemperatureContainerVisible = false;
    }
    // Only toggle if the mode is selected
    if (this.selectedModes.includes('context')) {
      this.isContextContainerVisible = !this.isContextContainerVisible;
    }
  }

  toggleTemperatureContainer(event?: Event): void {
    // Prevent event propagation if event exists
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Close context container if it's open
    if (this.isContextContainerVisible) {
      this.isContextContainerVisible = false;
    }
    // Only toggle if the mode is selected
    if (this.selectedModes.includes('temperature')) {
      this.isTemperatureContainerVisible = !this.isTemperatureContainerVisible;
    }
  }

  onContextChange(): void {
    this.contextText = this.contextText.trim();
    this.contextUpdated.emit(this.contextText);
  }

  onTemperatureChange(value: number): void {
    this.temperature = value;
    this.temperatureService.setTemperature('generalQuestions', value);
    this.temperatureChange.emit(value);
  }

  /** Enter commits the pending Tools selection while the popup is open. */
  @HostListener('document:keydown.enter', ['$event'])
  onEnterKey(event: Event): void {
    if (this.isPopupVisible) {
      this.commitPendingMode();
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    const target = event.target as HTMLElement;
    const clickedInsidePopup = target.closest('.mode-popup');
    const clickedInsideTools = target.closest('.tools-button');
    const clickedInsideContext = target.closest('.context-container');
    const clickedInsideContextButton = target.closest('.active-mode-button');
    const clickedInsideTemperature = target.closest('.temperature-container') || target.closest('.temperature-popover');
    const clickedInsideTemperatureButton = target.closest('.active-mode-button') || target.closest('.temperature-icon-btn');

    if (!clickedInsidePopup && !clickedInsideTools) {
      // Clicking outside the Tools popup commits whatever was tentatively chosen.
      this.commitPendingMode();
    }

    if (!clickedInsideContext && !clickedInsideContextButton) {
      this.isContextContainerVisible = false;
    }

    if (!clickedInsideTemperature && !clickedInsideTemperatureButton) {
      this.isTemperatureContainerVisible = false;
    }
  }
}
